<script setup lang="ts">
/**
 * QuickStart — 首页「快速上手」区块
 * 合并原 #install（单卡入口）+ #workflow（3 步 teaser）：
 * 电脑端 / 手机端 两个 tab，各 3 步直达观赛 + 友情提示条 + 完整指南 CTA。
 */
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import YfbIcon from '@/components/YfbIcon.vue'
import type { IconName } from '@/types/icons'
import { useAnchorNav } from '@/composables/useAnchorNav'

const { t, tm } = useI18n()
const { go } = useAnchorNav()

type Device = 'desktop' | 'mobile'

const device = ref<Device>('desktop')

const deviceTabs: { key: Device; label: string; icon: IconName }[] = [
  { key: 'desktop', label: t('home.quickStart.deviceTabs.desktop'), icon: 'ph:desktop-bold' },
  { key: 'mobile', label: t('home.quickStart.deviceTabs.mobile'), icon: 'ph:device-mobile-bold' }
]

const steps = computed<{ title: string; desc: string }[]>(() => {
  const r = tm(`home.quickStart.steps.${device.value}`) as any
  const arr = r?.value ?? r
  return Array.isArray(arr) ? arr : []
})

function handleLink(href: string) {
  void go(href)
}
</script>

<template>
  <div class="qs">
    <!-- 设备切换 -->
    <div class="qs__tabs" role="tablist" :aria-label="t('home.sections.quickStart')">
      <button
        v-for="d in deviceTabs"
        :key="d.key"
        type="button"
        role="tab"
        class="qs__tab"
        :class="{ 'qs__tab--active': device === d.key }"
        :aria-selected="device === d.key"
        @click="device = d.key"
      >
        <YfbIcon :name="d.icon" :size="14" />
        <span>{{ d.label }}</span>
      </button>
    </div>

    <!-- 三步 -->
    <div class="qs__steps">
      <div
        v-for="(step, idx) in steps"
        :key="`${device}-${idx}`"
        class="qs__step"
      >
        <div class="qs__num">{{ String(idx + 1).padStart(2, '0') }}</div>
        <div class="qs__body">
          <h3 class="qs__title">{{ step.title }}</h3>
          <p class="qs__desc">{{ step.desc }}</p>
        </div>
        <div v-if="idx < steps.length - 1" class="qs__arrow" aria-hidden="true">→</div>
      </div>
    </div>

    <!-- 友情提示 -->
    <aside class="qs__notice" role="note">
      <YfbIcon name="ph:warning-bold" :size="16" class="qs__notice-icon" />
      <p class="qs__notice-text">{{ t('home.quickStart.notice') }}</p>
    </aside>

    <!-- CTA -->
    <div class="qs__ctas">
      <a
        href="/install"
        class="qs__cta yfb-press"
        @click.prevent="handleLink('/install')"
      >
        {{ t('home.quickStart.ctaInstall') }}
        <span aria-hidden="true">→</span>
      </a>
      <a
        href="/guide"
        class="qs__cta qs__cta--ghost yfb-press"
        @click.prevent="handleLink('/guide')"
      >
        {{ t('home.quickStart.ctaGuide') }}
      </a>
    </div>
  </div>
</template>

<style scoped>
.qs {
  max-width: 1100px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: var(--yfb-sp-6);
}

/* 设备切换 */
.qs__tabs {
  display: flex;
  gap: var(--yfb-sp-2);
  justify-content: center;
}

.qs__tab {
  display: inline-flex;
  align-items: center;
  gap: var(--yfb-sp-2);
  padding: var(--yfb-sp-2) var(--yfb-sp-5);
  background-color: var(--yfb-surface);
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-md);
  color: var(--yfb-text-secondary);
  font-family: inherit;
  font-size: var(--yfb-fs-sm);
  font-weight: 700;
  letter-spacing: var(--yfb-ls-label);
  cursor: pointer;
  transition: border-color var(--yfb-anim-fast) ease-out, color var(--yfb-anim-fast) ease-out, background-color var(--yfb-anim-fast) ease-out;
}

.qs__tab:hover {
  border-color: var(--yfb-accent);
  color: var(--yfb-accent);
}

.qs__tab--active {
  background-color: var(--yfb-accent);
  border-color: var(--yfb-accent);
  color: var(--yfb-on-accent);
  box-shadow: 0 0 12px var(--yfb-glow-soft);
}

/* 三步 */
.qs__steps {
  display: flex;
  align-items: stretch;
  gap: var(--yfb-sp-6);
}

.qs__step {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: var(--yfb-sp-6);
  background-color: var(--yfb-surface);
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-lg);
  box-shadow: var(--yfb-shadow-2);
  position: relative;
  transition: border-color var(--yfb-anim-fast) ease-out, box-shadow var(--yfb-anim-fast) ease-out;
}

.qs__step:hover {
  border-color: var(--yfb-accent);
  box-shadow: 0 0 14px var(--yfb-glow);
}

.qs__num {
  display: inline-block;
  padding: var(--yfb-sp-1) var(--yfb-sp-3);
  background-color: var(--yfb-accent);
  color: var(--yfb-on-accent);
  font-family: var(--yfb-font-heading);
  font-size: var(--yfb-fs-xl);
  font-weight: 700;
  letter-spacing: var(--yfb-ls-mono);
  border-radius: var(--yfb-radius-sm);
  box-shadow: 0 0 10px var(--yfb-glow);
  align-self: flex-start;
  margin-bottom: 16px;
}

.qs__body {
  display: flex;
  flex-direction: column;
  gap: var(--yfb-sp-2);
  flex: 1;
}

.qs__title {
  font-family: var(--yfb-font-heading);
  font-size: var(--yfb-fs-lg);
  font-weight: 700;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: var(--yfb-ls-display);
}

.qs__desc {
  font-size: var(--yfb-fs-base);
  font-weight: 400;
  line-height: var(--yfb-lh-lead);
  margin: 0;
  color: var(--yfb-text-secondary);
}

.qs__arrow {
  position: absolute;
  right: -28px;
  top: 50%;
  transform: translateY(-50%);
  font-size: var(--yfb-fs-2xl);
  font-weight: 700;
  color: var(--yfb-accent);
  text-shadow: 0 0 10px var(--yfb-glow);
  z-index: 1;
}

/* 友情提示（全边框 + 弱化底，不用侧边条） */
.qs__notice {
  display: flex;
  align-items: flex-start;
  gap: var(--yfb-sp-3);
  padding: var(--yfb-sp-4) var(--yfb-sp-5);
  background-color: var(--yfb-surface-2);
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-md);
}

.qs__notice-icon {
  flex-shrink: 0;
  color: var(--yfb-signal-warning);
  margin-top: 2px;
}

.qs__notice-text {
  margin: 0;
  font-size: var(--yfb-fs-sm);
  line-height: var(--yfb-lh-body);
  color: var(--yfb-ink);
}

/* CTA */
.qs__ctas {
  display: flex;
  justify-content: center;
  gap: var(--yfb-sp-4);
  flex-wrap: wrap;
}

.qs__cta {
  display: inline-flex;
  align-items: center;
  gap: calc(var(--yfb-sp-2) + var(--yfb-sp-1) / 2);
  padding: var(--yfb-sp-4) var(--yfb-sp-7);
  background-image: var(--yfb-grad-cta);
  color: var(--yfb-on-accent);
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-md);
  box-shadow: 0 0 14px var(--yfb-glow);
  font-size: var(--yfb-fs-md);
  font-weight: 700;
  letter-spacing: var(--yfb-ls-mono);
  text-decoration: none;
  transition: box-shadow var(--yfb-anim-fast) ease-out, filter var(--yfb-anim-fast) ease-out, transform var(--yfb-anim-fast) ease-out;
}

.qs__cta:hover {
  filter: brightness(1.08);
  box-shadow: 0 0 22px var(--yfb-glow), 0 0 34px var(--yfb-glow-2);
  transform: translateY(-1px);
}

.qs__cta:focus-visible {
  outline: 2px solid var(--yfb-focus);
  outline-offset: 4px;
}

.qs__cta--ghost {
  background-image: none;
  background-color: var(--yfb-surface);
  color: var(--yfb-accent);
  box-shadow: none;
}

.qs__cta--ghost:hover {
  filter: none;
  box-shadow: 0 0 14px var(--yfb-glow);
  border-color: var(--yfb-accent);
}

@media (max-width: 767px) {
  .qs__steps {
    flex-direction: column;
    gap: var(--yfb-sp-4);
  }
  .qs__arrow {
    position: static;
    transform: rotate(90deg);
    text-align: center;
    margin: -8px 0;
  }
  .qs__cta {
    width: 100%;
    justify-content: center;
  }
}

@media (prefers-reduced-motion: reduce) {
  .qs__step,
  .qs__cta,
  .qs__tab {
    transition: none;
  }
}
</style>
